/**
 * PaperTexture - Creates a subtle paper grain noise overlay
 * Part of the "Tactile Minimalism" aesthetic
 */

import Phaser from 'phaser';

/**
 * Configuration for paper texture
 */
export interface PaperTextureConfig {
  /** Number of noise particles to generate */
  particleCount: number;
  /** Minimum alpha for noise particles */
  minAlpha: number;
  /** Maximum alpha for noise particles */
  maxAlpha: number;
  /** Minimum particle size */
  minSize: number;
  /** Maximum particle size */
  maxSize: number;
  /** Overall overlay alpha */
  overlayAlpha: number;
}

export const DEFAULT_PAPER_CONFIG: PaperTextureConfig = {
  particleCount: 12000,
  minAlpha: 0.02,
  maxAlpha: 0.05,
  minSize: 1,
  maxSize: 2,
  overlayAlpha: 0.25,
};

/**
 * Creates a paper grain texture overlay for the entire screen
 */
export class PaperTexture {
  private scene: Phaser.Scene;
  private texture: Phaser.GameObjects.RenderTexture | null = null;
  private overlay: Phaser.GameObjects.Image | null = null;
  private config: PaperTextureConfig;

  constructor(scene: Phaser.Scene, config: Partial<PaperTextureConfig> = {}) {
    this.scene = scene;
    this.config = { ...DEFAULT_PAPER_CONFIG, ...config };
    this.createTexture();
  }

  /**
   * Generate the paper grain noise texture
   */
  private createTexture(): void {
    const { width, height } = this.scene.scale;
    const {
      particleCount,
      minAlpha,
      maxAlpha,
      minSize,
      maxSize,
      overlayAlpha,
    } = this.config;

    // Create render texture for noise
    this.texture = this.scene.make.renderTexture({ width, height }, false);

    const graphics = this.scene.make.graphics({}, false);

    // Generate paper grain noise
    for (let i = 0; i < particleCount; i++) {
      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(0, height);
      const alpha = Phaser.Math.FloatBetween(minAlpha, maxAlpha);
      const size = Phaser.Math.Between(minSize, maxSize);

      // Mix of light and dark spots for paper texture
      const color = Math.random() > 0.5 ? 0x000000 : 0xffffff;

      graphics.fillStyle(color, alpha);
      graphics.fillRect(x, y, size, size);
    }

    // Add some subtle larger grain patches
    for (let i = 0; i < particleCount / 10; i++) {
      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(0, height);
      const alpha = Phaser.Math.FloatBetween(0.01, 0.02);
      const size = Phaser.Math.Between(2, 4);
      const color = Math.random() > 0.5 ? 0x000000 : 0xffffff;

      graphics.fillStyle(color, alpha);
      graphics.fillCircle(x, y, size);
    }

    this.texture.draw(graphics);
    graphics.destroy();

    // Create overlay image
    this.overlay = this.scene.add
      .image(0, 0, this.texture.texture)
      .setOrigin(0, 0)
      .setBlendMode(Phaser.BlendModes.OVERLAY)
      .setAlpha(overlayAlpha)
      .setDepth(1000);
  }

  /**
   * Set the depth of the overlay
   */
  setDepth(depth: number): void {
    if (this.overlay) {
      this.overlay.setDepth(depth);
    }
  }

  /**
   * Set visibility of the overlay
   */
  setVisible(visible: boolean): void {
    if (this.overlay) {
      this.overlay.setVisible(visible);
    }
  }

  /**
   * Get the overlay alpha
   */
  getAlpha(): number {
    return this.overlay?.alpha ?? 0;
  }

  /**
   * Set the overlay alpha
   */
  setAlpha(alpha: number): void {
    if (this.overlay) {
      this.overlay.setAlpha(alpha);
    }
  }

  /**
   * Destroy the texture and overlay
   */
  destroy(): void {
    if (this.overlay) {
      this.overlay.destroy();
      this.overlay = null;
    }
    if (this.texture) {
      this.texture.destroy();
      this.texture = null;
    }
  }
}
